import type { ContractSuretyAccountUnderwritingModelSize } from "@inrev/common";
import { FormRadioCardSelect } from "@portal/components/form/fields";
import type { RadioCardOption } from "@portal/components/patterns/radio-select";
import { Icon } from "@portal/components/primitives/Icon";
import type { Control, FieldValues, Path } from "react-hook-form";

type AccountExposureSizeSelectProps<TFieldValues extends FieldValues> = {
	name: Path<TFieldValues>;
	control: Control<TFieldValues>;
	disabledOptions?: ContractSuretyAccountUnderwritingModelSize[];
};

export const AccountExposureSizeSelect = <TFieldValues extends FieldValues>({
	name,
	control,
	disabledOptions,
}: AccountExposureSizeSelectProps<TFieldValues>) => {
	const isDisabled = (size: ContractSuretyAccountUnderwritingModelSize) =>
		disabledOptions?.includes(size);

	const options: RadioCardOption<ContractSuretyAccountUnderwritingModelSize>[] = [
		{
			value: "small",
			label: "Up to $500k",
			description: "Minimal eligibility details, credit score",
			footer: { text: "Fastest underwriting" },
			disabled: isDisabled("small"),
		},
		{
			value: "medium",
			label: "$500k - $1M",
			description: "Some financial details, some eligibility details, credit score",
			disabled: isDisabled("medium"),
		},
		{
			value: "large",
			label: "More than $1M",
			description: "Full financial details, full eligibility details, credit score",
			footer: {
				leadingIcon: <Icon type="arrow-up" color="confirm" size="micro" filled />,
				text: "Highest limits",
			},
			disabled: isDisabled("large"),
		},
	];

	return (
		<FormRadioCardSelect name={name} control={control} options={options} className="w-[510px]" />
	);
};
